import React, { createContext, useState, useMemo, useEffect, ReactNode } from "react";
import { createTheme, ThemeProvider, ButtonProps, PaletteMode, CircularProgress } from "@mui/material";
import WebSabitlerAPI from "../apis/WebSabitler";

type ThemeContextType = {
  primaryColor: string;
  setPrimaryColor: (color: string) => void;
  secondaryColor: string;
  setSecondaryColor: (color: string) => void;
  thirdColor: string;
  setThirdColor: (color: string) => void;
  fourthColor: string;
  setFourthColor: (color: string) => void;
  errorColor: string;
  setErrorColor: (color: string) => void;
  buttonVariant: ButtonProps['variant'];
  setButtonVariant: (variant: ButtonProps['variant']) => void;
  themeMode: PaletteMode;
  setThemeMode: (mode: PaletteMode) => void;
  fontSize: number;
  setFontSize: (size: number) => void;
  kvkkMetni: string; // Yeni eklenen
  ilIlceKodu:string;
  belediyeTelefon: string;
  belediyeMail: string;
  belediyeAdres: string;
  belediyeKurumAdi: string;
  loginTelKontrol: string;
  ilMi: string;
  reloadTheme: () => void;
};

declare module "@mui/material/styles" {
  interface Palette {
    third: Palette['primary'];
    fourth: Palette['primary'];
  }
  interface PaletteOptions {
    third?: PaletteOptions['primary'];
    fourth?: PaletteOptions['primary'];
  }
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const ThemeProviderComponent = ({ children }: { children: ReactNode }) => {
  const [primaryColor, setPrimaryColor] = useState<string>("#1976d2");
  const [secondaryColor, setSecondaryColor] = useState<string>("#ff2081");
  const [thirdColor, setThirdColor] = useState<string>("#00bcd4");
  const [fourthColor, setFourthColor] = useState<string>("#8e24aa");
  const [errorColor, setErrorColor] = useState<string>("#f44336");
  const [kvkkMetni, setKvkkMetni] = useState<string>("");
  const [ilIlceKodu, setIlIlceKodu] = useState<string>("");
  const [belediyeTelefon, setBelediyeTelefon] = useState<string>("");
  const [belediyeMail, setBelediyeMail] = useState<string>("");
  const [belediyeAdres, setBelediyeAdres] = useState<string>("");
  const [belediyeKurumAdi, setBelediyeKurumAdi] = useState<string>("");

  const [buttonVariant, setButtonVariant] = useState<ButtonProps['variant']>("contained");
  const [themeMode, setThemeMode] = useState<PaletteMode>("light");
  const [fontSize, setFontSize] = useState<number>(1.0);
  const [isLoading, setIsLoading] = useState(true);
  const [loginTelKontrol, setLoginTelKontrol] = useState<string>("");
  const [ilMi, setIlMi] = useState<string>("");

  const fetchThemeConfig = async () => {
    try {
      const response = await WebSabitlerAPI.getWebSabitler();
      if (response.data) {
        const data = response.data;
        console.log("Wegsabitler",data)
        setPrimaryColor(data.primaryColor || "#1976d2");
        setSecondaryColor(data.secondaryColor || "#ff2081");
        setThirdColor(data.thirdColor || "#00bcd4");
        setFourthColor(data.fourthColor || "#8e24aa");
        setErrorColor(data.errorColor || "#f44336");
        setButtonVariant(data.buttonVariant as ButtonProps['variant'] || "contained");
        setThemeMode(data.themeMode as PaletteMode || "light");
        setFontSize(data.fontSize || 1.0);
        setKvkkMetni(data.kvkMetni ||"")
        setIlIlceKodu(data.ilIlceKodu ||"")
        setBelediyeTelefon(data.belediyeTelefon || "");
        setBelediyeMail(data.belediyeMail || "");
        setBelediyeAdres(data.belediyeAdres || "");
        setBelediyeKurumAdi(data.belediyeKurumAdi || "");
        setLoginTelKontrol(data.loginTelKontrol || "");
        setIlMi(data.ilMi || "");
      } else {
        // API'den veri gelmezse varsayılan değerleri kullan
        setDefaultThemeValues();
      }
    } catch (error) {
      console.error("Theme config fetch error:", error);
      // Hata durumunda varsayılan değerleri kullan
      setDefaultThemeValues();
    } finally {
      setIsLoading(false);
    }
  };

  const reloadTheme = () => {
    fetchThemeConfig();
  };

  useEffect(() => {
    fetchThemeConfig();
  }, []);

  const setDefaultThemeValues = () => {
    const defaultConfig = {
      primaryColor: "#1976d2",
      secondaryColor: "#ff2081",
      thirdColor: "#00bcd4",
      fourthColor: "#8e24aa",
      errorColor: "#f44336",
      buttonVariant: "contained",
      themeMode: "light",
      fontSize: 1.0,
      kvkkMetni:"",
      ilIlceKodu:"",
      belediyeTelefon: "",
      belediyeMail: "",
      belediyeAdres: "",
      belediyeKurumAdi: "",
      loginTelKontrol: "",
      ilMi: "",
    };

    setPrimaryColor(defaultConfig.primaryColor);
    setSecondaryColor(defaultConfig.secondaryColor);
    setThirdColor(defaultConfig.thirdColor);
    setFourthColor(defaultConfig.fourthColor);
    setErrorColor(defaultConfig.errorColor);
    setButtonVariant(defaultConfig.buttonVariant as ButtonProps['variant']);
    setThemeMode(defaultConfig.themeMode as PaletteMode);
    setFontSize(defaultConfig.fontSize);
    setIlIlceKodu(defaultConfig.ilIlceKodu)
    setBelediyeTelefon(defaultConfig.belediyeTelefon);
    setBelediyeMail(defaultConfig.belediyeMail);
    setBelediyeAdres(defaultConfig.belediyeAdres);
    setBelediyeKurumAdi(defaultConfig.belediyeKurumAdi);
    setLoginTelKontrol(defaultConfig.loginTelKontrol);
    setIlMi(defaultConfig.ilMi);
  };

  // Theme güncelleme fonksiyonlarını API çağrısı yapacak şekilde güncelle
  const updateThemeInAPI = async (theme: Partial<ThemeContextType>) => {
    try {
      // API'ye güncelleme isteği gönder
      // Burada API'nin update fonksiyonu olduğunu varsayıyorum
      // await WebSabitlerAPI.updateWebSabitler(theme);
    } catch (error) {
      console.error("Theme update error:", error);
    }
  };

  // Handle fonksiyonları güncellendi
  const handlePrimaryColorChange = (color: string) => {
    setPrimaryColor(color);
    updateThemeInAPI({ primaryColor: color });
  };

  const handleSecondaryColorChange = (color: string) => {
    setSecondaryColor(color);
    updateThemeInAPI({ secondaryColor: color });
  };

  const handleThirdColorChange = (color: string) => {
    setThirdColor(color);
    updateThemeInAPI({ thirdColor: color });
  };

  const handleFourthColorChange = (color: string) => {
    setFourthColor(color);
    updateThemeInAPI({ fourthColor: color });
  };

  const handleErrorColorChange = (color: string) => {
    setErrorColor(color);
    updateThemeInAPI({ errorColor: color });
  };

  const handleButtonVariantChange = (variant: ButtonProps['variant']) => {
    setButtonVariant(variant);
    updateThemeInAPI({ buttonVariant: variant });
  };

  const handleThemeModeChange = (mode: PaletteMode) => {
    setThemeMode(mode);
    updateThemeInAPI({ themeMode: mode });
  };

  const handleFontSizeChange = (size: number) => {
    setFontSize(size);
    updateThemeInAPI({ fontSize: size });
  };

  const theme = useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: "Ubuntu, sans-serif",

        },
        palette: {
          mode: themeMode,
          primary: {
            main: primaryColor,
            contrastText: "#fff",
          },
          secondary: {
            main: secondaryColor,
            contrastText: "#fff",
          },
          error: {
            main: errorColor,
            contrastText: "#fff",
          },
          third: {
            main: thirdColor,
            contrastText: "#fff",
          },
          fourth: {
            main: fourthColor,
            contrastText: "#fff",
          },
        },
        components: {
          MuiTab: {
            styleOverrides: {
              root: {
                fontWeight: "bolder",
                textTransform: "none",
              },
            },
          },

          MuiTextField: {
            styleOverrides: {
              root: {
                "& .Mui-error": {
                  borderColor: errorColor,
                },
                "& .Mui-disabled": {
                  backgroundColor: themeMode === "light" ? "#f0f0f0" : "#333",
                },
              },
            },
          },
          MuiButton: {
            defaultProps: {
              variant: buttonVariant,
            },
            styleOverrides: {
              root: {
                paddingBottom: "0.46rem",
                paddingTop: "0.46rem",
                paddingLeft: "2rem",
                paddingRight: "2rem",
                textTransform: "none",
              },
              containedPrimary: {
                color: "#fff",
              },
              containedSecondary: {
                color: "#fff",
              },
              outlinedPrimary: {
                color: primaryColor,
              },
              outlinedSecondary: {
                color: secondaryColor,
              },
            },
          },
          MuiTableCell: {
            styleOverrides: {
              root: {
                fontWeight: "normal",
                border: '3px solid #ddd',  // Her hücreye ince bir gri kenarlık
              },
            },
          },


          MuiTablePagination: {
            defaultProps: {

              labelRowsPerPage: "Satır Sayısı",
            },
            styleOverrides: {
              root: {
                border: '3px solid #ddd', // dış border kalınlığını ve rengini ayarlıyoruz
                display: "flex",
                alignItems: "center",
              },
              toolbar: {
                display: "flex",
                alignItems: "center",
                paddingBottom: 0, // Alt marjinleri sıfırlayın
                paddingTop: 0, // Üst marjinleri sıfırlayın
              },
              actions: {
                display: "flex",
                alignItems: "center",
                marginBottom: 0, // Alt marjinleri sıfırlayın
                marginTop: 0, // Üst marjinleri sıfırlayın
              },
              selectLabel: {
                display: "flex",
                alignItems: "center",
                marginBottom: 0, // Alt marjinleri sıfırlayın
                marginTop: 0, // Üst marjinleri sıfırlayın
                marginRight: "8px", // İsteğe bağlı olarak sağa boşluk ekleyebilirsiniz
              },
              displayedRows: {
                display: "flex",
                alignItems: "center",
                marginBottom: 0, // Alt marjinleri sıfırlayın
                marginTop: 0, // Üst marjinleri sıfırlayın
              },
            },
          },
        },
      }),
    [primaryColor, secondaryColor, thirdColor, fourthColor, errorColor, buttonVariant, themeMode, fontSize]
  );

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <CircularProgress />
      </div>
    );
  }

  return (
    <ThemeContext.Provider
      value={{
        primaryColor,
        setPrimaryColor: handlePrimaryColorChange,
        secondaryColor,
        setSecondaryColor: handleSecondaryColorChange,
        thirdColor,
        setThirdColor: handleThirdColorChange,
        fourthColor,
        setFourthColor: handleFourthColorChange,
        errorColor,
        setErrorColor: handleErrorColorChange,
        buttonVariant,
        setButtonVariant: handleButtonVariantChange,
        themeMode,
        setThemeMode: handleThemeModeChange,
        fontSize,
        setFontSize: handleFontSizeChange,
        kvkkMetni,
        ilIlceKodu,
        belediyeTelefon,
        belediyeMail,
        belediyeAdres,
        belediyeKurumAdi,
        loginTelKontrol,
        ilMi,
        reloadTheme,
      }}
    >
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </ThemeContext.Provider>
  );
};

export { ThemeProviderComponent, ThemeContext };
